import CategoryRequest from '@/http/CategoryRequest'
import ItemRequest from '@/http/ItemRequest'
import MenuRequest from '@/http/MenuRequest'
import config from '../../config/config'
import { convertLocalDateTime } from '@/utility/time'

const menu = {
  namespaced: true,
  state: {
    images: [],
    categories: [],
    items: [],
    draftMenu: {
      // 下書き用のメニュー
      menuId: null,
      startAt: '',
      endAt: '',
      menuCategoryList: [],
      status: '',
    },
    inOperationMenu: {
      // 本番用のメニュー
      menuId: null,
      startAt: '',
      endAt: '',
      menuCategoryList: [],
      status: '',
    },
    currentCategory: {
      // 現在アクティブなカテゴリー
      draft: {
        id: 0,
        names: [],
      },
      inOperation: {
        id: 0,
        names: [],
      },
    },
    dispalyMode: 0,
    isShowItemSelectModal: false, // 商品追加モーダル表示フラグ
    isShowPreviewModal: false, // プレビューモーダル表示フラグ
  },
  getters: {
    // カテゴリー一覧の取得
    getCategories: (state) => {
      return state.categories
    },
    // 商品一覧の取得
    getItems: (state) => {
      return state.items
    },
    // 下書きメニューの取得
    getDraftMenu: (state) => {
      return state.draftMenu
    },
    // 運用中メニューの取得
    getInOperationMenu: (state) => {
      return state.inOperationMenu
    },
    // メニューの商品をカテゴリーコードで絞り込んで取得する
    getMenuItemsByCategoryCode: (state) => (categoryCode) => {
      let category = {}
      if (state.dispalyMode === config.menuStatus.draft) {
        category = state.draftMenu.menuCategoryList.find(
          (category) => category.categoryId === categoryCode
        )
      } else if (state.dispalyMode === config.menuStatus.inOperation) {
        category = state.inOperationMenu.menuCategoryList.find(
          (category) => category.categoryId === categoryCode
        )
      }

      if (Object.keys(category).length > 0) {
        return category.itemList
      }
      return []
    },
    // 現在の画面表示モードの取得
    getDisplayMode: (state) => {
      return state.dispalyMode
    },
    // 現在アクティブなカテゴリーコードを取得
    getCurrentCategoryCode: (state) => {
      if (state.dispalyMode === config.menuStatus.draft) {
        return state.currentCategory.draft.id
      }
      if (state.dispalyMode === config.menuStatus.inOperation) {
        return state.currentCategory.inOperation.id
      }
    },
    // 現在アクティブなカテゴリー名を取得
    getCurrentCategoryName: (state, _getters_, _rootState_, rootGetters) => {
      const language = rootGetters['settings/getLanguage']
      const defaultLanguage = rootGetters['settings/getDefaultLanguage']
      if (state.dispalyMode === config.menuStatus.draft) {
        return (
          state.currentCategory.draft.names[language] ||
          state.currentCategory.draft.names[defaultLanguage]
        )
      }
      if (state.dispalyMode === config.menuStatus.inOperation) {
        return (
          state.currentCategory.inOperation.names[language] ||
          state.currentCategory.inOperation.names[defaultLanguage]
        )
      }
    },
    // 商品追加モーダルの表示フラグの取得
    getIsShowItemSelectModal: (state) => {
      return state.isShowItemSelectModal
    },
    // プレビューモーダルの表示フラグの取得
    getIsShowPreviewModal: (state) => {
      return state.isShowPreviewModal
    },
    // 商品コードで画像を取得する
    getImageByItemCode: (state) => (itemCode) => {
      return state.images[`item-${itemCode}`]
    },
  },
  actions: {
    // APIからカテゴリー一覧を取得
    fetchCategories: ({ commit }) => {
      const request = new CategoryRequest()
      request
        .getList()
        .then((res) => {
          const payload = {
            id: res.data.list[0].categoryCode,
            names: res.data.list[0].names,
          }
          commit('setDraftCurrentCategory', payload)
          commit('setInOperationCurrentCategory', payload)
          commit('setCategories', res.data.list)
        })
        .catch((error) => {
          console.log(error.response)
          throw new Error(error)
        })
    },
    // APIから商品一覧を取得
    fetchItems: ({ commit }) => {
      const request = new ItemRequest()
      request
        .getList()
        .then((res) => {
          let images = {}
          const itemList = res.data.list.map((item) => {
            // itemCodeをキーに画像に別途保存する
            images[`item-${item.itemCode}`] = {
              normalImage: item.normalImage,
              largeImage: item.largeImage,
            }
            // base64部分を削除して軽くする
            return {
              category: item.category,
              itemCode: item.itemCode,
              itemKind: item.itemKind,
              names: item.names,
              toppings: item.toppings,
              isPriceChange: item.isPriceChange,
              unit: item.unit,
            }
          })
          commit('setItems', itemList)
          commit('setImages', images)
        })
        .catch((error) => {
          console.log(error.response)
          throw new Error(error)
        })
    },
    // APIからメニューを取得
    fetchMenu: ({ commit }) => {
      const request = new MenuRequest()
      request.getList().then((res) => {
        let draftMenu = {}
        let inOperationMenu = {}
        res.data.list.forEach((menu) => {
          // 下書き
          if (
            menu.status === config.menuStatus.draft ||
            (menu.status === config.menuStatus.inOperation &&
              global.moment().isBefore(convertLocalDateTime(menu.startAt)))
          ) {
            draftMenu = menu
          }

          // 運用中
          if (
            menu.status === config.menuStatus.inOperation &&
            global.moment().isAfter(convertLocalDateTime(menu.startAt)) &&
            global.moment().isBefore(convertLocalDateTime(menu.endAt))
          ) {
            inOperationMenu = menu
          }
        })

        commit('setDraftMenu', draftMenu)
        commit('setInOperationMenu', inOperationMenu)
      })
    },
    // 下書きを新規登録する
    createDraft: function({ commit }) {
      const request = new MenuRequest()
      request.insert().then((res) => {
        const menu = res.data
        commit('setDraftMenu', menu)
      })
    },
    // 現在アクティブなカテゴリーの更新
    updateCurrentCategoryCode: function(context, payload) {
      const category = context.state.categories.find(
        (category) => category.categoryCode === payload
      )
      if (category === undefined) {
        console.log('invalid categoryCode', payload)
        throw new Error('invalid categoryCode')
      }

      const currentCategory = {
        id: payload,
        names: category.names,
      }
      if (context.state.dispalyMode === config.menuStatus.draft) {
        context.commit('setDraftCurrentCategory', currentCategory)
      } else if (context.state.dispalyMode === config.menuStatus.inOperation) {
        context.commit('setInOperationCurrentCategory', currentCategory)
      }
    },
    // 画面の表示モードの切替
    updateDisplayMode: function({ commit }, payload) {
      commit('setDisplayMode', payload)
    },
    // 商品追加モーダルの表示切替
    updateIsShowItemSelectModal: ({ commit }, payload) => {
      commit('setIsShowItemSelectModal', payload)
    },
    // プレビューモーダルの表示切替
    updateIsShowPreviewModal: ({ commit }, payload) => {
      commit('setIsShowPreviewModal', payload)
    },
    // カテゴリーから商品を削除する
    deleteItem: function({ commit }, payload) {
      commit('deleteMenuItem', payload)
    },
    // カテゴリーに商品を追加する
    addMenuItem: function({ commit }, payload) {
      commit('addMenuItem', payload)
    },
    // メニューの商品を更新する(wide,表示非表示を想定)
    updateMenuItem: ({ commit }, payload) => {
      commit('updateMenuItem', payload)
    },
    // 特定のカテゴリーのメニューを置き換える
    // ※ ソートしたもので置き換える際の使用を想定
    sortMenuItem: ({ commit }, payload) => {
      commit('sortMenuItem', payload)
    },
  },
  mutations: {
    setCategories(state, payload) {
      state.categories = payload
    },
    setItems(state, payload) {
      state.items = payload
    },
    setImages(state, payload) {
      state.images = payload
    },
    setDraftMenu(state, payload) {
      state.draftMenu = payload
    },
    setInOperationMenu(state, payload) {
      state.inOperationMenu = payload
    },
    setDraftCurrentCategory(state, payload) {
      state.currentCategory.draft = payload
    },
    setInOperationCurrentCategory(state, payload) {
      state.currentCategory.inOperation = payload
    },
    setDisplayMode(state, payload) {
      state.dispalyMode = payload
    },
    setIsShowItemSelectModal(state, payload) {
      state.isShowItemSelectModal = payload
    },
    setIsShowPreviewModal(state, payload) {
      state.isShowPreviewModal = payload
    },
    addMenuItem(state, { itemCode, language }) {
      let menu
      let categoryId
      if (state.dispalyMode === config.menuStatus.draft) {
        menu = state.draftMenu.menuCategoryList
        categoryId = state.currentCategory.draft.id
      } else if (state.dispalyMode === config.menuStatus.inOperation) {
        menu = state.inOperationMenu.menuCategoryList
        categoryId = state.currentCategory.inOperation.id
      }

      let categoryKey
      menu.forEach((category, index) => {
        if (category.categoryId === categoryId) {
          categoryKey = index
        }
      })

      // 重複する商品コードは追加しない
      let item = menu[categoryKey].itemList.find(
        (item) => item.itemCode === itemCode
      )
      if (item !== undefined) {
        return
      }

      // 追加商品の検索
      item = state.items.find((item) => item.itemCode === itemCode)
      if (item === undefined) {
        return
      }

      const addItem = {
        itemCode,
        name: item.names[language],
        sortOrder: 0,
        wideView: 0,
        unVisibleFlag: 0,
      }
      menu[categoryKey].itemList.unshift(addItem)
    },
    updateMenuItem(state, payload) {
      let menu
      let categoryId
      if (state.dispalyMode === config.menuStatus.draft) {
        menu = state.draftMenu.menuCategoryList
        categoryId = state.currentCategory.draft.id
      } else if (state.dispalyMode === config.menuStatus.inOperation) {
        menu = state.inOperationMenu.menuCategoryList
        categoryId = state.currentCategory.inOperation.id
      }

      let categoryKey
      menu.forEach((category, index) => {
        if (category.categoryId === categoryId) {
          categoryKey = index
        }
      })

      let itemKey
      menu[categoryKey].itemList.forEach((item, index) => {
        if (item.itemCode === payload.itemCode) {
          itemKey = index
        }
      })

      menu[categoryKey].itemList[itemKey] = payload
    },
    deleteMenuItem(state, payload) {
      let menu
      let categoryId
      if (state.dispalyMode === config.menuStatus.draft) {
        menu = state.draftMenu.menuCategoryList
        categoryId = state.currentCategory.draft.id
      } else if (state.dispalyMode === config.menuStatus.inOperation) {
        menu = state.inOperationMenu.menuCategoryList
        categoryId = state.currentCategory.inOperation.id
      }

      let categoryKey
      menu.forEach((category, index) => {
        if (category.categoryId === categoryId) {
          categoryKey = index
        }
      })

      let itemKey
      menu[categoryKey].itemList.forEach((item, index) => {
        if (item.itemCode === payload) {
          itemKey = index
        }
      })
      menu[categoryKey].itemList.splice(itemKey, 1)
    },
    sortMenuItem(state, payload) {
      let menu
      let categoryId
      if (state.dispalyMode === config.menuStatus.draft) {
        menu = state.draftMenu.menuCategoryList
        categoryId = state.currentCategory.draft.id
      } else if (state.dispalyMode === config.menuStatus.inOperation) {
        menu = state.inOperationMenu.menuCategoryList
        categoryId = state.currentCategory.inOperation.id
      }

      let categoryKey
      menu.forEach((category, index) => {
        if (category.categoryId === categoryId) {
          categoryKey = index
        }
      })
      menu[categoryKey].itemList = payload
    },
  },
}

export default menu
